import { assignRoles, checkHealth } from '@/plugins/api'
import { authService } from '@/services/AuthService'
import { useDefaultStore } from '@/stores/defaultStore'
import { useUserStore } from '@/stores/userStore'
import router from '@/router'
import { loadConfig } from './ConfigService'

class AppLoader {
  private initPromise: Promise<void> | null = null

  async initialize(): Promise<void> {
    // Return existing promise if initialization is already in progress
    if (this.initPromise) {
      return this.initPromise
    }

    this.initPromise = this.performInitialization()
    return this.initPromise
  }

  private async performInitialization(): Promise<void> {
    console.log('[AppLoaderService]: Initialization')
    const isLocalServiceMode = localStorage.getItem('serviceMode') === 'true'

    try {
      // Get config
      console.info('[AppLoaderService]: Getting and validating config')
      await loadConfig().catch((error) => {
        router.push('/error?title=Invalid configuration structure&description=It looks like the configuration is not correct')
        throw error
      })

      /**
       * 1. Check if service mode is locally set
       * 2. If yes, redirect to service-mode
       */
      if (isLocalServiceMode && router.currentRoute.value.name !== 'service-mode') {
        router.push('/service-mode')
      }

      // Check backend health
      console.log('[AppLoaderService]: Checking health')
      if (!await checkHealth()) {
        console.debug('Health check error')
        if (!isLocalServiceMode) {
          router.push('/error?status=500&title=Server not responding&description=There seems to be a problem connecting to the server')
          throw 'Health check error'
        }
      }

      // Initialize MSAL and authenticate
      await authService.initialize()
      const response = await authService.handleRedirectPromise()
      
      if (!response) {
        await authService.loginRedirect()
        return // Will redirect, no need to continue
      }

      // Fetch user data and assign roles
      const token = await authService.getAccessToken()
      
      if (token) {
        const userStore = useUserStore()
        await userStore.getUser()
        userStore.roles = assignRoles(token)
      }

      this.finish()
    }
    
    catch (error) {
      console.error('App initialization failed:', error)
      throw error
    }

    finally {
      this.initPromise = null
    }
  }

  private finish() {
    const defaultStore = useDefaultStore()
    defaultStore.isInitialized = true
    defaultStore.isLoading = false
  }
}

export const appLoader = new AppLoader()